import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useLang } from "@/i18n";
import { useCart } from "@/context/CartContext";
import { NaguaraLogo } from "@/components/BrandMark";
import { EN_ENABLED } from "@/lib/config";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const { count, setIsOpen } = useCart();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const panelRef = useRef(null);
  const burgerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  // Mobile menu: Escape closes and returns focus, Tab stays inside the panel.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        burgerRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const inside = panelRef.current?.querySelectorAll("a[href], button:not([disabled])");
      if (!inside?.length) return;
      const list = [burgerRef.current, ...inside].filter(Boolean);
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    panelRef.current?.querySelector("a[href], button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const links = [
    { id: "coffees", to: "/#coffees", label: t.nav.coffees },
    { id: "hosteleria", to: "/hosteleria", label: t.nav.horeca },
    { id: "academia", to: "/formacion", label: t.nav.academia },
    { id: "story", to: "/historia", label: t.nav.story },
    { id: "contact", to: "/contacto", label: t.nav.contact },
  ];

  const setLanguage = (l) => {
    setLang(l);
    localStorage.setItem("naguara_lang", l);
  };

  const LangSwitch = () => (
    <div
      data-testid="lang-switch"
      className="flex items-center gap-1.5 text-xs font-bold tracking-widest text-[#F5F1E8]"
      role="group"
      aria-label={lang === "es" ? "Idioma" : "Language"}
    >
      <button
        data-testid="lang-es"
        onClick={() => setLanguage("es")}
        aria-pressed={lang === "es"}
        className={`min-w-[44px] min-h-[44px] transition-opacity ${lang === "es" ? "opacity-100" : "opacity-45 hover:opacity-80"}`}
      >
        ES
      </button>
      <span className="opacity-30">/</span>
      <button
        data-testid="lang-en"
        onClick={() => setLanguage("en")}
        aria-pressed={lang === "en"}
        className={`min-w-[44px] min-h-[44px] transition-opacity ${lang === "en" ? "opacity-100" : "opacity-45 hover:opacity-80"}`}
      >
        EN
      </button>
    </div>
  );

  // outside the home page the background is light, so the bar is always solid
  const solid = scrolled || mobileOpen || location.pathname !== "/";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-[#422D28] shadow-lg shadow-[#422D28]/20" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="logo-btn"
          className="flex items-center transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] rounded"
          aria-label="Naguarà"
        >
          <NaguaraLogo variant="beige" width={138} className="w-[128px] md:w-[152px]" />
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className="text-sm font-medium tracking-wide whitespace-nowrap text-[#F5F1E8]/90 transition-colors hover:text-[#F4D35E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] rounded px-1 py-2"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/#coffees"
            data-testid="nav-buy"
            className="bg-[#F4D35E] text-[#3B2523] text-sm font-semibold whitespace-nowrap px-5 py-2.5 rounded-full transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5F1E8] focus-visible:ring-offset-2 focus-visible:ring-offset-[#422D28]"
          >
            {t.nav.shop}
          </Link>
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          {EN_ENABLED && (
            <div className="hidden lg:block">
              <LangSwitch />
            </div>
          )}
          <button
            data-testid="cart-open-btn"
            onClick={() => setIsOpen(true)}
            aria-label={t.cart.title}
            className="relative w-11 h-11 flex items-center justify-center text-[#F5F1E8] rounded-full transition-colors hover:bg-[#F5F1E8]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E]"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute top-1 right-1 bg-[#F4D35E] text-[#3B2523] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            ref={burgerRef}
            className="lg:hidden w-11 h-11 flex items-center justify-center text-[#F5F1E8] rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E]"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            aria-label={lang === "es" ? "Menú" : "Menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="mobile-menu"
          ref={panelRef}
          data-testid="mobile-menu"
          className="lg:hidden bg-[#422D28] border-t border-[#F5F1E8]/10 px-6 py-4 flex flex-col"
        >
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`mnav-${l.id}`}
              onClick={() => setMobileOpen(false)}
              className="flex items-center min-h-[48px] text-lg font-medium text-[#F5F1E8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] rounded"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/#coffees"
            data-testid="mnav-buy"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center justify-center min-h-[48px] mt-4 mb-2 self-start bg-[#F4D35E] text-[#3B2523] text-base font-semibold px-6 py-3 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5F1E8] focus-visible:ring-offset-2 focus-visible:ring-offset-[#422D28]"
          >
            {t.nav.shop}
          </Link>
          {EN_ENABLED && (
            <div className="pt-2">
              <LangSwitch />
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
